// Only the changes needed in your CreateServiceOrder.tsx

import { logEvent } from "@shared/analytics/analytics";
import { Button } from "@shared/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateServiceOrder, useServiceProcedureList } from "../../hooks"; // Same hook, just enhanced
import { SOPTemplate } from "../../types";
import { mapApiDataToSOPTemplate } from "../../utils";
import { MainContentPanel } from "./component/MainContentPanel";
import { SOPTemplatesPanel } from "./component/SOPTemplatesPanel";
import { SuccessModal } from "./component/SuccessModal";

const CreateServiceOrder = () => {
  const navigate = useNavigate();
  const [selectedSOP, setSelectedSOP] = useState<SOPTemplate | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<any[]>([]);
  const [priority, setPriority] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState<any>(null);

  // ✅ ONLY CHANGE: Enable URL-based filtering
  const {
    data: apiResponse,
    isLoading,
    error,
    refetch,
  } = useServiceProcedureList(
    {
      isActive: true,
      limit: 50,
    },
    true // ← Enable URL-based filtering
  );

  // Service order creation hook
  const createServiceOrderMutation = useCreateServiceOrder();

  // Transform API data to SOPTemplate format
  const sopTemplates = useMemo(() => {
    if (!apiResponse?.results) return [];

    return apiResponse.results.map(mapApiDataToSOPTemplate);
  }, [apiResponse]);

  const handleCancel = () => {
    navigate("/service-orders/dispatcher");
  };

  const handleCreateOrder = () => {
    if (selectedSOP && selectedEntities.length > 0 && priority) {
      createServiceOrderMutation.mutate(
        {
          selectedSOP,
          selectedEntities,
          priority,
          notes,
          fieldForceId: selectedTechnician?.fieldForceId,
        },
        {
          onSuccess: () => {
            logEvent("SOP Created", {
              selectedSOP: selectedSOP.name,
              priority,
              fieldForceId: selectedTechnician?.fieldForceId,
            });
            setShowSuccessModal(true);
          },
        }
      );
    }
  };

  const handleCreateAnother = () => {
    setSelectedSOP(null);
    setSelectedEntities([]);
    setPriority("");
    setNotes("");
    setShowSuccessModal(false);
    setSelectedTechnician(null);
    createServiceOrderMutation.reset();
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    navigate("/service-orders/dispatcher");
  };

  const isValid = selectedSOP && selectedEntities.length > 0 && priority;
  const isCreating = createServiceOrderMutation.isPending;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-4 h-15">
        <div className="flex justify-between flex-row-reverse gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-2"
            disabled={isLoading || isCreating}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dispatcher
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Create Service Order
            </h1>
            <p className="text-sm text-gray-600">
              Select SOP template and entities to create a new service order
              {apiResponse && (
                <span className="text-blue-600 ml-1">
                  ({apiResponse.count} templates available)
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-88px)]">
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          {/* ✅ ONLY CHANGE: Add isLoading prop */}
          <SOPTemplatesPanel
            templates={sopTemplates}
            selectedSOP={selectedSOP}
            onSelectSOP={setSelectedSOP}
            totalCount={apiResponse?.count || 0}
            hasMore={!!apiResponse?.next}
            isLoading={isLoading} // ← Add loading state
          />
        </div>

        {/* Right Panel - Main Content */}
        <div className="flex-1 flex flex-col bg-gray-50">
          <MainContentPanel
            selectedSOP={selectedSOP}
            selectedEntities={selectedEntities}
            onEntitiesChange={setSelectedEntities}
            priority={priority}
            onPriorityChange={setPriority}
            notes={notes}
            onNotesChange={setNotes}
            onTechnicianSelect={setSelectedTechnician}
          />

          {/* Action Buttons */}
          <div className="bg-white border-t border-gray-200 px-6 py-4">
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleCancel}
                className="text-gray-600 border-gray-300 hover:bg-gray-50"
                disabled={isLoading || isCreating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateOrder}
                disabled={!isValid || isLoading || isCreating}
                className={`${
                  isValid && !isLoading && !isCreating
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                } transition-colors`}
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating Order...
                  </>
                ) : isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Loading...
                  </>
                ) : (
                  "Create Service Order"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={handleCloseSuccessModal}
          selectedSOP={selectedSOP}
          selectedEntities={selectedEntities}
          priority={priority}
          notes={notes}
          onCreateAnother={handleCreateAnother}
        />
      )}
    </div>
  );
};

export default CreateServiceOrder;
